import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { notificationApi } from '../../services/notification-api';
import { useContentComparison } from '../../hooks/useContentComparison';
import { BranchLiveDialog } from './BranchLiveDialog';

const SEEN_STORAGE_KEY = 'echo:branch-live-celebrated-ids';
const SEEN_LIMIT = 50;

function loadSeen(): string[] {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveSeen(ids: string[]) {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(ids.slice(0, SEEN_LIMIT)));
  } catch {
    // localStorage may be unavailable (private mode, quota, etc.) — fail open.
  }
}

function extractBranchName(message: string): string | undefined {
  // Backend format: `"{branchName}" has been published`
  const match = message.match(/"([^"]+)"/);
  return match?.[1];
}

/**
 * Polls for unread `content_published` notifications and surfaces a
 * one-time celebration dialog the first time the recipient sees one.
 * The notification only reaches users who own or collaborate on the branch,
 * so the celebration fires even when an admin author publishes their own work.
 */
export function BranchLivePresenter() {
  const { isAuthenticated, user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pending, setPending] = useState<{
    id: string;
    branchName?: string;
    branchId?: string;
  } | null>(null);
  const seenRef = useRef<Set<string>>(new Set(loadSeen()));

  const { data } = useQuery({
    queryKey: ['notifications', 'branch-live-celebration'],
    queryFn: () =>
      notificationApi.list({ type: 'content_published', isRead: false, page: 1, limit: 10 }),
    refetchInterval: 30_000,
    enabled: isAuthenticated,
    staleTime: 10_000,
  });

  useEffect(() => {
    if (!isAuthenticated || !user || dialogOpen || !data?.items?.length) return;

    const candidate = data.items.find((n) => !seenRef.current.has(n.id));

    if (!candidate) return;

    setPending({
      id: candidate.id,
      branchName: extractBranchName(candidate.message),
      branchId: candidate.resourceType === 'branch' ? candidate.resourceId : undefined,
    });
    setDialogOpen(true);

    const next = new Set(seenRef.current);
    next.add(candidate.id);
    seenRef.current = next;
    saveSeen(Array.from(next));
  }, [data, dialogOpen, isAuthenticated, user]);

  // Pre-fetch the branch's content comparison so "View it" can navigate
  // straight to one of the published articles (the first piece of content
  // in the diff) rather than dumping the user on the library landing.
  // The comparison query stays around until the branch's data is purged
  // — branch rows survive `archive` even though no further edits are
  // possible, so this read works post-publish.
  const { data: comparison } = useContentComparison(pending?.branchId);
  // Deep-link to the content's slug (not its title — `path` is the title).
  // Skip deleted files (their slug no longer resolves on main) and landing
  // pages (no `/:slug` route, and they carry no content slug).
  const firstContentSlug =
    comparison?.files.find(
      (f) => (f.fileType === 'content' || !f.fileType) && f.status !== 'deleted' && f.slug
    )?.slug ?? null;

  if (!isAuthenticated) return null;

  return (
    <BranchLiveDialog
      open={dialogOpen}
      branchName={pending?.branchName}
      branchId={pending?.branchId}
      firstContentSlug={firstContentSlug}
      onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setPending(null);
      }}
    />
  );
}

export default BranchLivePresenter;
